/**
 * Service for submitting feedback directly to JSM/Socrates without the modal.
 * This replicates the @atlaskit/feedback-collector submission logic for V2 inline feedback.
 *
 * Endpoint: /gateway/api/feedback-collector-api/v2/feedback
 * Data flows to Socrates table: platform_product_feedback_collector
 */

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { FetchError, performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import { type FeedbackTrigger } from './types';

// Use btoa for base64 encoding in browser
const base64Encode = (str: string): string => {
	try {
		return btoa(unescape(encodeURIComponent(str)));
	} catch {
		// Fallback for environments where btoa might not work
		return str;
	}
};

//link to the Jira Board: https://customerfeedback.atlassian.net/jira/servicedesk/projects/AM/queues/custom/922
const JSM_FIELD_IDS = {
	summary: 'summary',
	description: 'description',
	customerName: 'customfield_10045',
	type: 'customfield_10042',
	canBeContacted: 'customfield_10043',
	enrollInResearch: 'customfield_10044',
	feedbackContext: 'customfield_10047',
} as const;

const JSM_FIELD_VALUES = {
	typeComment: { id: '10106' },
	typeBug: { id: '10105' },
	typeSuggestion: { id: '10107' },
	typeQuestion: { id: '10108' },

	canBeContactedAgree: [{ id: '10109' }],
	canBeContactedDecline: [{ id: '10111' }],

	enrollInResearchAgree: [{ id: '10110' }],
	enrollInResearchDecline: [{ id: '10112' }],
} as const;

export type CsatRating = 'completely' | 'mostly' | 'somewhat' | 'barely' | 'not-at-all';

export interface JSMFeedbackMetadata {
	aaid: string;
	answerQuality: 'good' | 'bad';
	assistantResponseId?: string;
	messageRequestId?: string;
	origin: string;
	history?: string | object;
	feedbackSource?: string;
	feedbackTrigger?: FeedbackTrigger;
	selectedAgent?: {
		id?: string;
		externalConfigReference?: string;
	};
	conversationId?: string;
	categories?: string[];
	additionalFeedback?: string;
	includeMessages?: boolean;
	enrollInResearch?: boolean;
	isDetailedFeedback?: boolean;
	minionAlias?: string;
	csatRating?: CsatRating;
}

export interface JSMFeedbackParams {
	entryPointId: string;
	metadata: JSMFeedbackMetadata;
	customerName?: string;
	email?: string;
}

interface FeedbackField {
	id: string;
	value: string | object | object[];
}

interface FeedbackBody {
	feedback: {
		entrypointId: string;
		entrypointUiVersion: string;
		fields: FeedbackField[];
	};
}

const PACKAGE_VERSION = '1.0.0';

/**
 * Maps feedback categories to JSM feedback type.
 */
const mapCategoriesToFeedbackType = (
	categories: string[] | undefined,
): (typeof JSM_FIELD_VALUES)[keyof typeof JSM_FIELD_VALUES] => {
	if (!categories || categories.length === 0) {
		return JSM_FIELD_VALUES.typeComment;
	}

	const bugCategories = ['inaccurate', 'outOfDate'];
	if (categories.some((cat) => bugCategories.includes(cat))) {
		return JSM_FIELD_VALUES.typeBug;
	}

	const suggestionCategories = ['tooLong', 'tooShort', 'poorlySourced'];
	if (categories.some((cat) => suggestionCategories.includes(cat))) {
		return JSM_FIELD_VALUES.typeSuggestion;
	}

	return JSM_FIELD_VALUES.typeComment;
};

/**
 * Submits feedback directly to JSM/Feedback Collector API.
 * Supports both quick feedback (thumbs up/down) and detailed feedback (with categories and text).
 * This allows V2 feedback to appear in Socrates (platform_product_feedback_collector table).
 */
export const submitQuickFeedbackToJSM = async ({
	entryPointId,
	metadata,
	customerName = 'unknown',
	email,
}: JSMFeedbackParams): Promise<{ success: boolean }> => {
	const isDetailedFeedback = metadata.isDetailedFeedback ?? false;

	const userFeedbackText = metadata.additionalFeedback || '';

	const description = `${userFeedbackText}

\`\`\`
${JSON.stringify(metadata, null, 2)}
\`\`\`
`;

	const feedbackPrefix = metadata.answerQuality === 'good' ? 'Positive' : 'Negative';

	let summary = '';
	if (metadata.categories && metadata.categories.length > 0) {
		summary = `${feedbackPrefix}: ${metadata.categories.join(', ')}`;
	} else if (userFeedbackText) {
		const truncatedText = userFeedbackText.replace(/\n/g, ' ').slice(0, 100);
		summary =
			truncatedText.length < userFeedbackText.length ? `${truncatedText}...` : truncatedText;
	} else {
		summary = `${feedbackPrefix} feedback - Quick thumbs`;
	}

	const canBeContactedValue = metadata.includeMessages
		? JSM_FIELD_VALUES.canBeContactedAgree
		: JSM_FIELD_VALUES.canBeContactedDecline;

	const enrollInResearchValue = metadata.enrollInResearch
		? JSM_FIELD_VALUES.enrollInResearchAgree
		: JSM_FIELD_VALUES.enrollInResearchDecline;

	const feedbackTypeValue = isDetailedFeedback
		? mapCategoriesToFeedbackType(metadata.categories)
		: JSM_FIELD_VALUES.typeComment;

	const feedbackContextParts: string[] = [];
	if (email) {
		feedbackContextParts.push(`email: ${email}`);
	}
	feedbackContextParts.push(`atlassian-account-id: ${metadata.aaid}`);
	if (metadata.origin) {
		feedbackContextParts.push(`origin: ${metadata.origin}`);
	}
	if (metadata.feedbackSource) {
		feedbackContextParts.push(`feedbackSource: ${metadata.feedbackSource}`);
	}
	if (metadata.feedbackTrigger) {
		feedbackContextParts.push(`feedbackTrigger: ${metadata.feedbackTrigger}`);
	}
	if (metadata.categories && metadata.categories.length > 0) {
		feedbackContextParts.push(`categories: ${metadata.categories.join(', ')}`);
	}
	const feedbackContextValue = feedbackContextParts.join('\n');

	const fields: FeedbackField[] = [
		{
			id: JSM_FIELD_IDS.type,
			value: feedbackTypeValue,
		},
		{
			id: JSM_FIELD_IDS.summary,
			value: summary,
		},
		{
			id: JSM_FIELD_IDS.description,
			value: description,
		},
		...(metadata.includeMessages
			? [
					{
						id: 'aaidOrHash',
						value: metadata.aaid,
					},
				]
			: []),
		{
			id: JSM_FIELD_IDS.customerName,
			value: customerName,
		},
		{
			id: JSM_FIELD_IDS.canBeContacted,
			value: canBeContactedValue,
		},
		{
			id: JSM_FIELD_IDS.enrollInResearch,
			value: enrollInResearchValue,
		},
		...(feedbackContextValue
			? [
					{
						id: JSM_FIELD_IDS.feedbackContext,
						value: feedbackContextValue,
					},
				]
			: []),
	];

	const body: FeedbackBody = {
		feedback: {
			entrypointId: entryPointId,
			entrypointUiVersion: PACKAGE_VERSION,
			fields,
		},
	};

	const postData = base64Encode(JSON.stringify(body));

	try {
		await performHttpFetch({
			path: '/gateway/api/feedback-collector-api/v2/feedback',
			options: {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ data: postData }),
			},
		});
	} catch (err) {
		const statusCode = err instanceof FetchError ? err.errorAttributes?.statusCode : undefined;
		logException({
			exception: err instanceof Error ? err : new Error(String(err)),
			name: 'feedbackSubmissionError',
			tags: {
				feedbackSubmissionError: 'jsm-feedback',
				...(statusCode ? { httpStatusCode: String(statusCode) } : {}),
			},
		});
		throw err;
	}

	return { success: true };
};
